/**
 * Merge field utilities for Contributing Assets workflow
 * Extracts and formats data from workflow_data JSONB for document generation
 */

interface WorkflowData {
  // Universal fields
  asset_type: string;
  contributor_name: string;
  contributor_role: string;
  contributor_address: string;
  contributor_country?: string;
  contribution_date: string;
  contribution_method: string;
  receiving_entity: string;
  contribution_purpose: string;
  
  // UNA data (included when workflow is launched)
  una_name?: string;
  una_state?: string;
  
  // Business Entity fields
  entity_legal_name?: string;
  entity_type?: string;
  entity_state_of_formation?: string;
  entity_formation_date?: string;
  entity_ein?: string;
  entity_business_purpose?: string;
  ownership_percentage?: number;
  manager_name?: string;
  manager_title?: string;
  
  // Real Estate fields
  property_address?: string;
  property_city?: string;
  property_state?: string;
  property_zip?: string;
  property_county?: string;
  property_type_detail?: string;
  property_apn?: string;
  property_description?: string;
  deed_type?: string;
  property_purpose?: string[];
  
  // Vehicle fields
  vehicle_year?: string;
  vehicle_make?: string;
  vehicle_model?: string;
  vehicle_vin?: string;
  vehicle_type?: string;
  vehicle_color?: string;
  vehicle_purpose?: string;
  
  // IP fields
  ip_type?: string;
  ip_description?: string;
  
  // Digital Asset fields
  digital_asset_type?: string;
  asset_description?: string;
  blockchain_network?: string;
  
  // Cash fields
  contribution_description?: string;
  bank_name?: string;
  account_type?: string;
  
  // Personal Property fields
  property_category?: string;
  property_location?: string;
  property_condition?: string;
  
  // Other
  other_asset_description?: string;
  special_instructions?: string;
}

interface UnaData {
  name?: string;
  state?: string;
  una_name?: string;
  una_state?: string;
  una_formation_date?: string;
  treasury_secretary_id?: string;
  treasury_secretary_name?: string;
  treasury_secretary_address?: string;
  primary_officer_role?: string;
  board_members?: Array<{
    id: string;
    name: string;
    role?: string;
  }>;
}

export function getUniversalMergeFields(
  workflowData: WorkflowData,
  unaData: UnaData
): Record<string, string> {
  const currentDate = new Date().toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
  
  // Get the primary officer role, defaulting to "Secretary Treasurer"
  const primaryOfficerRole = unaData.primary_officer_role || 'Secretary Treasurer';
  
  const fields: Record<string, string> = {
    '{{una_name}}': unaData.name || workflowData.una_name || '',
    '{{UNA_NAME}}': unaData.name || workflowData.una_name || '',
    '{{una_state}}': unaData.state || workflowData.una_state || '',
    '{{una_formation_date}}': unaData.una_formation_date || '',
    '{{treasury_secretary_id}}': unaData.treasury_secretary_id || '',
    '{{treasury_secretary_name}}': unaData.treasury_secretary_name || workflowData.contributor_name || '',
    '{{treasury_secretary_address}}': unaData.treasury_secretary_address || '',
    '{{primary_officer_role}}': primaryOfficerRole,
    '{{contributor_name}}': workflowData.contributor_name || '',
    '{{contributor_role}}': workflowData.contributor_role || '',
    '{{contributor_address}}': workflowData.contributor_address || '',
    '{{contributor_country}}': workflowData.contributor_country || '',
    '{{contribution_date}}': workflowData.contribution_date 
      ? new Date(workflowData.contribution_date).toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'long',
          day: 'numeric'
        })
      : '',
    '{{contribution_method}}': workflowData.contribution_method || '',
    '{{receiving_entity}}': workflowData.receiving_entity || '',
    '{{contribution_purpose}}': workflowData.contribution_purpose || '',
    '{{current_date}}': currentDate,
    '{{asset_type}}': workflowData.asset_type || ''
  };
  
  // Add board member fields (up to 10)
  if (unaData.board_members && Array.isArray(unaData.board_members)) {
    unaData.board_members.forEach((member, index) => {
      const num = index + 1;
      if (num <= 10) {
        fields[`{{board_member_${num}_id}}`] = member.id || '';
        fields[`{{board_member_${num}_name}}`] = member.name || '';
      }
    });
  }
  
  return fields;
}

export function getBusinessEntityMergeFields(workflowData: WorkflowData): Record<string, string> {
  return {
    '{{entity_legal_name}}': workflowData.entity_legal_name || '',
    '{{entity_type}}': workflowData.entity_type || '',
    '{{entity_state_of_formation}}': workflowData.entity_state_of_formation || '',
    '{{entity_formation_date}}': workflowData.entity_formation_date 
      ? new Date(workflowData.entity_formation_date).toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'long',
          day: 'numeric'
        })
      : '',
    '{{entity_ein}}': workflowData.entity_ein || 'N/A',
    '{{entity_business_purpose}}': workflowData.entity_business_purpose || '',
    '{{ownership_percentage}}': workflowData.ownership_percentage?.toString() || '100',
    '{{manager_name}}': workflowData.manager_name || '',
    '{{manager_title}}': workflowData.manager_title || 'Manager'
  };
}

export function getRealEstateMergeFields(workflowData: WorkflowData): Record<string, string> {
  return {
    '{{property_address}}': workflowData.property_address || '',
    '{{property_city}}': workflowData.property_city || '',
    '{{property_state}}': workflowData.property_state || '',
    '{{property_zip}}': workflowData.property_zip || '',
    '{{property_county}}': workflowData.property_county || '',
    '{{property_type_detail}}': workflowData.property_type_detail || '',
    '{{property_apn}}': workflowData.property_apn || 'N/A',
    '{{property_description}}': workflowData.property_description || '',
    '{{deed_type}}': workflowData.deed_type || 'Quitclaim Deed',
    '{{property_purpose}}': Array.isArray(workflowData.property_purpose)
      ? workflowData.property_purpose.join(', ')
      : workflowData.property_purpose || ''
  };
}

export function getVehicleMergeFields(workflowData: WorkflowData): Record<string, string> {
  return {
    '{{vehicle_year}}': workflowData.vehicle_year || '',
    '{{vehicle_make}}': workflowData.vehicle_make || '',
    '{{vehicle_model}}': workflowData.vehicle_model || '',
    '{{vehicle_vin}}': workflowData.vehicle_vin || '',
    '{{vehicle_type}}': workflowData.vehicle_type || '',
    '{{vehicle_color}}': workflowData.vehicle_color || '',
    '{{vehicle_purpose}}': workflowData.vehicle_purpose || 'Provide transportation for officers',
    '{{vehicle_description}}': `${workflowData.vehicle_year || ''} ${workflowData.vehicle_make || ''} ${workflowData.vehicle_model || ''}`.trim()
  };
}

export function getIPMergeFields(workflowData: WorkflowData): Record<string, string> {
  return {
    '{{ip_type}}': workflowData.ip_type || '',
    '{{ip_description}}': workflowData.ip_description || ''
  };
}

export function getDigitalAssetMergeFields(workflowData: WorkflowData): Record<string, string> {
  return {
    '{{digital_asset_type}}': workflowData.digital_asset_type || '',
    '{{asset_description}}': workflowData.asset_description || '',
    '{{blockchain_network}}': workflowData.blockchain_network || 'N/A'
  };
}

export function getCashMergeFields(workflowData: WorkflowData): Record<string, string> {
  return {
    '{{contribution_description}}': workflowData.contribution_description || '',
    '{{bank_name}}': workflowData.bank_name || 'N/A',
    '{{account_type}}': workflowData.account_type || ''
  };
}

export function getPersonalPropertyMergeFields(workflowData: WorkflowData): Record<string, string> {
  return {
    '{{property_description}}': workflowData.asset_description || workflowData.property_description || '',
    '{{property_category}}': workflowData.property_category || '',
    '{{property_location}}': workflowData.property_location || '',
    '{{property_condition}}': workflowData.property_condition || ''
  };
}

/**
 * Get all merge fields for a specific asset type
 */
export function getAllMergeFields(
  workflowData: WorkflowData,
  unaData: UnaData
): Record<string, string> {
  const universal = getUniversalMergeFields(workflowData, unaData);
  
  // Add asset-specific merge fields based on asset type
  if (workflowData.asset_type?.includes('Business Entity')) {
    return { ...universal, ...getBusinessEntityMergeFields(workflowData) };
  } else if (workflowData.asset_type?.includes('Real Estate')) {
    return { ...universal, ...getRealEstateMergeFields(workflowData) };
  } else if (workflowData.asset_type?.includes('Vehicle')) {
    return { ...universal, ...getVehicleMergeFields(workflowData) };
  } else if (workflowData.asset_type === 'Intellectual Property') {
    return { ...universal, ...getIPMergeFields(workflowData) };
  } else if (workflowData.asset_type === 'Digital/Cryptocurrency') {
    return { ...universal, ...getDigitalAssetMergeFields(workflowData) };
  } else if (workflowData.asset_type === 'Cash/Bank Account') {
    return { ...universal, ...getCashMergeFields(workflowData) };
  } else if (workflowData.asset_type === 'Personal Property') {
    return { ...universal, ...getPersonalPropertyMergeFields(workflowData) };
  } else if (workflowData.asset_type === 'Other Asset') {
    return {
      ...universal,
      '{{other_asset_description}}': workflowData.other_asset_description || workflowData.asset_description || ''
    };
  }
  
  return universal;
}

/**
 * Replace merge fields in HTML template
 */
export function replaceMergeFields(html: string, mergeFields: Record<string, string>): string {
  let result = html;
  
  for (const [field, value] of Object.entries(mergeFields)) {
    // Replace all instances of the merge field
    result = result.replace(new RegExp(field.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'g'), value);
  }
  
  return result;
}
